import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Stack,
  FormHelperText,
  IconButton,
  Paper,
  FormControlLabel,
  Switch,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { motion } from "framer-motion";
import { NoticeboardButton } from "../shared";
import { useAccessibleCircles } from "../../hooks/useAccessibleCircles";
import {
  listMobileChipAudiences,
  noticeAudiencePickPatch,
  selectedMobileChipAudienceId,
  settleNoticeAudience,
  writeNoticeAudiencePatch,
} from "../../domain/notices/noticeAudience";
import {
  POLL_MAX_OPTIONS,
  POLL_MIN_OPTIONS,
  type PollOption,
} from "../../domain/polls/types";
import { pollChoicesProblem } from "../../domain/notices/noticeRules";

const COMPOSER_ENTER = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.52, ease: [0.22, 1, 0.36, 1] as const },
};

const SECTION_ENTER = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
};

/** Matches `PublishScheduleOptions` section headings. */
const sectionHeadingSx = {
  fontWeight: 400,
  color: "primary.main",
  fontSize: { xs: "1.125rem", sm: "1.25rem" },
};

function emptyOption(index: number): PollOption {
  return { optionId: `opt-${index}`, label: "", order: index };
}

type ComposerErrors = {
  title?: string;
  pollOptions?: string;
  audience?: string;
  targetSubcircle?: string;
};

interface MobilePollComposerProps {
  formData: {
    title: string;
    audience?: string;
    targetSubcircle?: string;
    pollOptions?: PollOption[];
    pollMode?: "standard" | "claim";
    publishMode?: "now" | "schedule";
    publishAt?: string;
  };
  errors: ComposerErrors;
  isSubmitting?: boolean;
  handleChange: (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
      | { target: { name: string; value: unknown } }
  ) => void;
  handleSubmit: (
    e: React.FormEvent,
    skipQualityCheck?: boolean,
    forceSubmit?: boolean
  ) => Promise<void>;
  noticeFlowId?: string;
}

function validatePollComposer(
  formData: MobilePollComposerProps["formData"]
): ComposerErrors {
  const next: ComposerErrors = {};
  const title = formData.title?.trim() || "";
  if (!title) {
    next.title = "Question is required";
  }

  const choices = pollChoicesProblem((formData.pollOptions || []).map((o) => o.label));
  if (choices) next.pollOptions = choices;

  if (!formData.audience) {
    next.audience = "Please select who should receive this poll";
  }
  if (
    formData.audience === "subcircle" &&
    !formData.targetSubcircle?.trim()
  ) {
    next.targetSubcircle = "Please select a target subcircle";
  }

  return next;
}

const MobilePollComposer: React.FC<MobilePollComposerProps> = ({
  formData,
  errors: parentErrors,
  isSubmitting,
  handleChange,
  handleSubmit,
  noticeFlowId,
}) => {
  const isClaimsFlow = noticeFlowId === "claims";
  const { subcircles, canAccessMainCircle } = useAccessibleCircles();
  const audienceCatalog = useMemo(
    () => ({
      canChooseEveryone: canAccessMainCircle,
      circles: subcircles,
    }),
    [canAccessMainCircle, subcircles],
  );
  const [localErrors, setLocalErrors] = useState<ComposerErrors>({});
  const errors = useMemo(
    () => ({ ...parentErrors, ...localErrors }),
    [parentErrors, localErrors]
  );

  const options: PollOption[] =
    formData.pollOptions?.length
      ? formData.pollOptions
      : [emptyOption(0), emptyOption(1)];

  const setOptions = (next: PollOption[]) => {
    handleChange({
      target: {
        name: "pollOptions",
        value: next.map((o, i) => ({
          ...o,
          order: i,
          optionId: o.optionId || `opt-${i}`,
        })),
      },
    });
  };

  React.useEffect(() => {
    const settled = settleNoticeAudience("mobileChips", audienceCatalog, {
      audience: formData.audience,
      targetSubcircle: formData.targetSubcircle,
    });
    if (settled) {
      writeNoticeAudiencePatch(settled, undefined, handleChange);
    }
  }, [
    audienceCatalog,
    formData.audience,
    formData.targetSubcircle,
    handleChange,
  ]);

  const selectAudience = (value: string) => {
    writeNoticeAudiencePatch(
      noticeAudiencePickPatch(value),
      undefined,
      handleChange,
    );
  };

  const selectedAudienceValue = selectedMobileChipAudienceId(
    {
      audience: formData.audience,
      targetSubcircle: formData.targetSubcircle,
    },
    canAccessMainCircle,
  );
  const mobileChipAudiences = listMobileChipAudiences(audienceCatalog);

  const updateOptionLabel = (index: number, label: string) => {
    const next = [...options];
    next[index] = { ...next[index], label };
    setOptions(next);
  };

  const addOption = () => {
    if (options.length >= POLL_MAX_OPTIONS) return;
    setOptions([...options, emptyOption(options.length)]);
  };

  const removeOption = (index: number) => {
    if (options.length <= POLL_MIN_OPTIONS) return;
    setOptions(options.filter((_, i) => i !== index));
  };

  const onSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validatePollComposer(formData);
    setLocalErrors(validation);
    if (Object.keys(validation).length > 0) return;
    setLocalErrors({});
    await handleSubmit(e, false, true);
  };

  return (
    <Box
      component="form"
      onSubmit={onSubmitForm}
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        height: "100%",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        component={motion.div}
        initial={COMPOSER_ENTER.initial}
        animate={COMPOSER_ENTER.animate}
        transition={COMPOSER_ENTER.transition}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2,
            pr: { xs: 9, sm: 2 },
            flexShrink: 0,
            pt: {
              xs: "calc(env(safe-area-inset-top, 0px) + 52px)",
              sm: 2,
            },
            pb: 1,
          }}
        >
          <Typography
            variant="h5"
            sx={{ color: "primary.main", fontWeight: 500 }}
          >
            {isClaimsFlow ? "Claims" : "Poll"}
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ px: 2.5, pt: 2.25, pb: 2.5, overflowY: "auto", flex: 1 }}>
          <Box
            component={motion.div}
            initial={SECTION_ENTER.initial}
            animate={SECTION_ENTER.animate}
            transition={{ duration: 0.34, ease: "easeOut", delay: 0.11 }}
          >
            <TextField
              fullWidth
              size="small"
              name="title"
              placeholder={
                isClaimsFlow
                  ? "What can parents claim?"
                  : "What would you like to ask?"
              }
              value={formData.title || ""}
              onChange={handleChange}
              error={!!errors.title}
              helperText={errors.title}
              inputProps={{ maxLength: 200 }}
              sx={{ mb: 2.5 }}
            />
          </Box>

          <Box
            component={motion.div}
            initial={SECTION_ENTER.initial}
            animate={SECTION_ENTER.animate}
            transition={{ duration: 0.34, ease: "easeOut", delay: 0.14 }}
            sx={{ mb: 2.5 }}
          >
            <Typography variant="h6" sx={{ ...sectionHeadingSx, mb: 1 }}>
              {isClaimsFlow
                ? "Choose who should receive this claim list"
                : "Choose who should receive this poll"}
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "nowrap",
                gap: 1,
                overflowX: "auto",
                overflowY: "hidden",
                pb: 0.25,
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {mobileChipAudiences.map((chip) => {
                const isSelected = selectedAudienceValue === chip.id;
                return (
                  <Button
                    key={chip.id}
                    type="button"
                    onClick={() => selectAudience(chip.id)}
                    sx={{
                      flexShrink: 0,
                      px: 1.5,
                      py: 0.45,
                      minWidth: "fit-content",
                      borderRadius: 999,
                      border: "1px solid",
                      borderColor: isSelected ? "primary.main" : "divider",
                      backgroundColor: isSelected
                        ? "primary.50"
                        : "background.paper",
                      color: isSelected ? "primary.main" : "text.secondary",
                      textTransform: "none",
                      fontWeight: 500,
                      fontSize: "0.82rem",
                    }}
                  >
                    {chip.label}
                  </Button>
                );
              })}
            </Box>

            {(errors.audience || errors.targetSubcircle) && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 0.75 }}
              >
                {errors.audience || errors.targetSubcircle}
              </Typography>
            )}
          </Box>

          <Box
            component={motion.div}
            initial={SECTION_ENTER.initial}
            animate={SECTION_ENTER.animate}
            transition={{ duration: 0.34, ease: "easeOut", delay: 0.2 }}
          >
            <Typography variant="h6" sx={{ ...sectionHeadingSx, mb: 1 }}>
              Answer choices
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.25 }}>
              Add between {POLL_MIN_OPTIONS} and {POLL_MAX_OPTIONS} options.
            </Typography>

            {errors.pollOptions && (
              <FormHelperText error sx={{ mb: 1 }}>
                {errors.pollOptions}
              </FormHelperText>
            )}

            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                borderColor: "divider",
              }}
            >
              {options.map((opt, index) => (
                <Box
                  key={opt.optionId || index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1.25,
                    py: 0.75,
                    borderTop: index > 0 ? "1px solid" : "none",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ width: 18, flexShrink: 0, textAlign: "center" }}
                  >
                    {index + 1}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={`Option ${index + 1}`}
                    value={opt.label}
                    onChange={(e) => updateOptionLabel(index, e.target.value)}
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                  />
                  <IconButton
                    size="small"
                    aria-label="Remove option"
                    disabled={options.length <= POLL_MIN_OPTIONS}
                    onClick={() => removeOption(index)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Paper>

            {options.length < POLL_MAX_OPTIONS && (
              <Button
                type="button"
                startIcon={<AddIcon />}
                onClick={addOption}
                sx={{ mt: 1.25, textTransform: "none", fontWeight: 600 }}
                size="small"
              >
                Add option
              </Button>
            )}

            {!isClaimsFlow && (
              <FormControlLabel
                sx={{ mt: 2, alignItems: "flex-start", ml: 0 }}
                control={
                  <Switch
                    checked={formData.pollMode === "claim"}
                    onChange={(event) =>
                      handleChange({
                        target: {
                          name: "pollMode",
                          value: event.target.checked ? "claim" : "standard",
                        },
                      })
                    }
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ pl: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      One claim per option
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Options disappear after someone selects them.
                    </Typography>
                  </Box>
                }
              />
            )}
          </Box>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            px: 2,
            py: 1.5,
            pb: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <NoticeboardButton
            type="submit"
            variant="primary"
            disabled={!!isSubmitting}
            sx={{
              width: "100%",
              py: 1.25,
              fontSize: "1rem",
              fontWeight: 600,
            }}
          >
            {isSubmitting
              ? "Posting…"
              : isClaimsFlow
                ? "Post claim list"
                : "Post Poll"}
          </NoticeboardButton>
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            justifyContent="center"
            sx={{ mt: 1 }}
          >
            <LockOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary">
              {isClaimsFlow
                ? "Review your claim list before posting"
                : "Review your poll details before posting"}
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default MobilePollComposer;
